import React, { useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import LeftNavbar from "./left_navbar";
import { AccessProvider, ProtectedRoute } from "./utils/AccessContext";

import ThankYou from "./Thank_you";
import SupportTickets from "./Support_Tickets";
import DefectTickets from "./Defect_Tickets";
import IdeaTickets from "./Idea_Tickets";
import Login from "./Login";
import EmployeeCenter from "./Employee_Center";
import EmployeeConfiguration from "./Employee_configuration";
import ManagerAllocation from "./Manager_Allocation";
import DSprCycleScheduler from "./dSpr_CycleScheduler";
import DSprReviewManagement from "./dSpr_ReviewManagement";
import DSprEmailTemplates from "./dSpr_EmailTemplates";
import DSprEmailLogs from "./dSpr_EmailLogs";
import DSprReports from "./dSpr_Reports";
import DSprReviewForm from "./dSpr_ReviewForm";
import DNewsNewsletter from "./dNews_Newsletter";
import DNewsTemplates from "./dNews_Templates";
import DNewsReminder from "./dNews_Reminder";
import DNewsHistory from "./dNews_History";
import DNewsEmailList from "./dNews_EmailList";
import { DNewsAlertHost } from "./utils/dNews_Alert";
import Dashboard from "./Dashboard";
import Holiday from "./Holiday";
import ProjectConfiguration from "./Project_configuration";
import LeaveConfiguration from "./Leave_configuration";
import Leave_approval_Levels from "./Leave_approval_Levels";
import Leave_approvals from "./Leave_approvals";
import Timesheet_approvals from "./Timesheet_approvals";
import Shifts from "./Shifts";
import Assign_Shifts from "./Assign_Shifts";
import Leave_dashboard from "./Leave_dashboard";
import Access_level_configuration from "./Access_level_configuration";
import Emp_App_Access from "./Emp_App_Access";
import App_visibility from "./App_visibility";
import Login_Banner_config from "./Login_Banner_config";
import Dolluzcorp_Banner_config from "./Dolluzcorp_Banner_config";
import Dolluzcorp_Updates_config from "./Dolluzcorp_Updates_config";
import Dolluzcorp_Policies_config from "./Dolluzcorp_Policies_config";
import Dolluzcorp_Blog_config from "./Dolluzcorp_Blog_config";
import CreateNotification from "./CreateNotification";
import Payslip_Salary_Structure from "./Payslip_Salary_Structure";
import Payslip_Generate from "./Payslip_Generate";
import Payslip_Management from "./Payslip_Management";
import Payslip_Components from "./Payslip_Components";
import Emp_Form16_Files from "./Emp_Form16_Files";
import Emp_Healthcard_Files from "./Emp_Healthcard_Files";
import Emp_E_Letter_Files from "./Emp_E_Letter_Files";
import Reimbursement_Admin from "./Reimbursement_Admin";
import Download_History from "./Download_History";
import DAttendanceManage from "./dAttendance_Manage";
import DAttendanceApprovals from "./dAttendance_Approvals";
import DAttendanceHistory from "./dAttendance_History";
import DFormsTemplates from "./dForms_Templates";

function App() {
  const [navSize, setNavSize] = useState("full");
  const location = useLocation();

  // ✅ Hide LeftNavbar on specific pages.
  // /dspr/review is the public self-review form - full-bleed, no sidebar.
  // startsWith handles trailing-slash + query-string variations.
  const path = location.pathname.toLowerCase();
  const shouldHideNavbar = path === "/login" || path === "/thank-you" || path.startsWith("/dspr/review");

  // ✅ Optional: force scroll to top on route change (helps prevent UI glitches)
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // ✅ Click anywhere in a date/time field opens the native picker — not just
  // the little calendar icon. One delegated listener covers every such input
  // on every page (including any added later), so the behaviour is consistent
  // app-wide. showPicker() needs a user gesture (this click qualifies) and is
  // wrapped in try/catch because clicking the native icon can also fire this
  // and re-invoking mid-open throws harmlessly.
  React.useEffect(() => {
    const PICKER_TYPES = new Set(["date", "datetime-local", "time", "month", "week"]);
    const openPicker = (e) => {
      const el = e.target;
      if (
        el && el.tagName === "INPUT" && PICKER_TYPES.has(el.type) &&
        !el.disabled && !el.readOnly && typeof el.showPicker === "function"
      ) {
        try { el.showPicker(); } catch { /* already open / not allowed — ignore */ }
      }
    };
    document.addEventListener("click", openPicker);
    return () => document.removeEventListener("click", openPicker);
  }, []);

  return (
    // AccessProvider fetches /api/AccessCenter/get-access + the logged-in
    // employee once on mount, and exposes hasAccess(category, page) to
    // every descendant. Single source of truth for navbar visibility and
    // route guards. Mounted at the App root so it survives across all
    // route changes (no re-fetch on navigation).
    <AccessProvider>
      {/* dNews custom toast + modal host - mounted once, reachable from
          any dNews page via dnAlert.success/error/confirm/prompt. */}
      <DNewsAlertHost />
      {/* ✅ Render Login & Thank You separately */}
      {shouldHideNavbar ? (
        <Routes>
          <Route path="/" element={<Navigate to="/Login" />} />
          <Route path="/Login" element={<Login />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/dspr/review" element={<DSprReviewForm />} />
          {/* Catch-all (non-authenticated session) - bounce unknown paths to login */}
          <Route path="*" element={<Navigate to="/Login" replace />} />
        </Routes>
      ) : (
        // ✅ Main layout only for logged-in sections
        <div className="main-layout">
          <LeftNavbar navSize={navSize} setNavSize={setNavSize} />
          <div className={`main-content ${navSize}`}>
            <Routes>
              <Route path="/" element={<Navigate to="/Login" />} />

              {/* Every protected route is wrapped in <ProtectedRoute category page>.
                  The wrapper reads /api/AccessCenter/get-access (cached in
                  AccessContext) and matches against the logged-in user's role.
                  If the matrix says the role can access (category, page), the
                  page renders. Otherwise the wrapper redirects to /login.
                  This makes Access Level Configuration the single source of
                  truth for both navbar visibility AND direct-URL access. */}
              <Route path="/Dashboard" element={
                <ProtectedRoute category="dAdmin" page="Dashboard">
                  <Dashboard navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* dForms */}
              <Route path="/dForms/Templates" element={
                <ProtectedRoute category="dForms" page="Templates">
                  <DFormsTemplates navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* dTime */}
              <Route path="/Holiday" element={
                <ProtectedRoute category="dTime" page="Holiday">
                  <Holiday navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Project_configuration" element={
                <ProtectedRoute category="dTime" page="Project Configuration">
                  <ProjectConfiguration navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Leave_configuration" element={
                <ProtectedRoute category="dTime" page="Leave Configuration">
                  <LeaveConfiguration navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Leave_approval_Levels" element={
                <ProtectedRoute category="dTime" page="Leave Approval Levels">
                  <Leave_approval_Levels navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Leave_approvals" element={
                <ProtectedRoute category="dTime" page="Leave Approvals">
                  <Leave_approvals navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Timesheet_approvals" element={
                <ProtectedRoute category="dTime" page="Timesheet Approvals">
                  <Timesheet_approvals navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Shifts" element={
                <ProtectedRoute category="dTime" page="Shifts">
                  <Shifts navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Assign_Shifts" element={
                <ProtectedRoute category="dTime" page="Assign Shifts">
                  <Assign_Shifts navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Leave_dashboard" element={
                <ProtectedRoute category="dTime" page="Leave Dashboard">
                  <Leave_dashboard navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* Manage Employee */}
              <Route path="/Employee_Center" element={
                <ProtectedRoute category="Manage Employee" page="Employee Center">
                  <EmployeeCenter navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Employee_configuration" element={
                <ProtectedRoute category="Manage Employee" page="Employee Configuration">
                  <EmployeeConfiguration navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* dSpr */}
              <Route path="/Manager_Allocation" element={
                <ProtectedRoute category="dSpr" page="Manager Allocation">
                  <ManagerAllocation navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dSpr_CycleScheduler" element={
                <ProtectedRoute category="dSpr" page="Cycle Scheduler">
                  <DSprCycleScheduler navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dSpr_ReviewManagement" element={
                <ProtectedRoute category="dSpr" page="Review Management">
                  <DSprReviewManagement navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dSpr_EmailTemplates" element={
                <ProtectedRoute category="dSpr" page="Email Templates">
                  <DSprEmailTemplates navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dSpr_EmailLogs" element={
                <ProtectedRoute category="dSpr" page="Email Logs">
                  <DSprEmailLogs navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dSpr_Reports" element={
                <ProtectedRoute category="dSpr" page="Reports">
                  <DSprReports navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* dNews */}
              <Route path="/dNews_Newsletter" element={
                <ProtectedRoute category="dNews" page="Newsletter">
                  <DNewsNewsletter navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dNews_Templates" element={
                <ProtectedRoute category="dNews" page="Presets">
                  <DNewsTemplates navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dNews_Reminder" element={
                <ProtectedRoute category="dNews" page="Reminder">
                  <DNewsReminder navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dNews_History" element={
                <ProtectedRoute category="dNews" page="History">
                  <DNewsHistory navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dNews_EmailList" element={
                <ProtectedRoute category="dNews" page="Email List">
                  <DNewsEmailList navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* dAssist / dBug */}
              <Route path="/Support_Tickets" element={
                <ProtectedRoute category="dAssist" page="Support Tickets">
                  <SupportTickets navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Defect_Tickets" element={
                <ProtectedRoute category="dBug" page="Defect Tickets">
                  <DefectTickets navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Idea_Tickets" element={
                <ProtectedRoute category="dBug" page="Idea Tickets">
                  <IdeaTickets navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* Access Center */}
              <Route path="/Access_level_configuration" element={
                <ProtectedRoute category="Access Center" page="Access Level Configuration">
                  <Access_level_configuration navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Emp_App_Access" element={
                <ProtectedRoute category="Access Center" page="App Configuration">
                  <Emp_App_Access navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/App_Visibility" element={
                <ProtectedRoute category="Inside D" page="App Visibility">
                  <App_visibility navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Login_Banner_config" element={
                <ProtectedRoute category="Inside D" page="Login Page Config">
                  <Login_Banner_config navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* Inside D (formerly Dolluzcorp Center) */}
              <Route path="/Dolluzcorp_Banner_config" element={
                <ProtectedRoute category="Inside D" page="Banner Configuration">
                  <Dolluzcorp_Banner_config navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Dolluzcorp_Updates_config" element={
                <ProtectedRoute category="Inside D" page="U/A Configuration">
                  <Dolluzcorp_Updates_config navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Dolluzcorp_Policies_config" element={
                <ProtectedRoute category="Inside D" page="Policies Configuration">
                  <Dolluzcorp_Policies_config navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Dolluzcorp_Blog_config" element={
                <ProtectedRoute category="Inside D" page="Blog Configuration">
                  <Dolluzcorp_Blog_config navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/CreateNotification" element={
                <ProtectedRoute category="Inside D" page="Notification Creation">
                  <CreateNotification navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* dSlip */}
              <Route path="/Payslip_Components" element={
                <ProtectedRoute category="dSlip" page="Payslip Components">
                  <Payslip_Components navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Payslip_Salary_Structure" element={
                <ProtectedRoute category="dSlip" page="Salary Structure">
                  <Payslip_Salary_Structure navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Payslip_Generate" element={
                <ProtectedRoute category="dSlip" page="Generate Payslips">
                  <Payslip_Generate navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Payslip_Management" element={
                <ProtectedRoute category="dSlip" page="Payslip Management">
                  <Payslip_Management navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Emp_Form16_Files" element={
                <ProtectedRoute category="dSlip" page="Emp Form-16 Files">
                  <Emp_Form16_Files navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Emp_Healthcard_Files" element={
                <ProtectedRoute category="dSlip" page="Emp Healthcard Files">
                  <Emp_Healthcard_Files navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Emp_E_Letter_Files" element={
                <ProtectedRoute category="dSlip" page="Emp E-Letter Files">
                  <Emp_E_Letter_Files navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Reimbursements" element={
                <ProtectedRoute category="dSlip" page="Reimbursements">
                  <Reimbursement_Admin navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/Download_History" element={
                <ProtectedRoute category="dSlip" page="Download History">
                  <Download_History navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* dAttendance */}
              <Route path="/dAttendance_Manage" element={
                <ProtectedRoute category="dAttendance" page="Work Pattern">
                  <DAttendanceManage navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dAttendance_Approvals" element={
                <ProtectedRoute category="dAttendance" page="Approvals">
                  <DAttendanceApprovals navSize={navSize} />
                </ProtectedRoute>
              } />
              <Route path="/dAttendance_History" element={
                <ProtectedRoute category="dAttendance" page="Activity History">
                  <DAttendanceHistory navSize={navSize} />
                </ProtectedRoute>
              } />

              {/* Catch-all - anything unknown bounces back to login. */}
              <Route path="*" element={<Navigate to="/Login" replace />} />
            </Routes>
          </div>
        </div>
      )}
    </AccessProvider>
  );
}

export default App;
